import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Privacy Policy — draft for the Saudi market (PDPL-aligned).
// IMPORTANT: This is a working draft. Have a Saudi-licensed lawyer review
// before publishing publicly or signing customer contracts.
// ─────────────────────────────────────────────────────────────────────────────

export default function Privacy() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const navigate = useNavigate();
  const lastUpdated = 'May 2026';

  return (
    <div className="min-h-screen bg-white text-[#0E1320]" dir={isAr ? 'rtl' : 'ltr'} data-testid="privacy-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E6EAF0]">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[13px] text-[#4A5263] hover:text-[#0E1320]" data-testid="privacy-back">
            <ArrowLeft className="h-3.5 w-3.5" />
            {isAr ? 'رجوع' : 'Back'}
          </button>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#4A5263]">
            <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
            REVA · {isAr ? 'سياسة الخصوصية' : 'Privacy'}
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 prose prose-sm reva-prose">
        <h1 className="text-3xl font-semibold tracking-tight">
          {isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}
        </h1>
        <span
          className="inline-flex items-center gap-1.5 mt-3 text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#4A5263] bg-[#F1F4F7] border border-[#E1E6EC] px-2.5 py-1 rounded-full"
          data-testid="privacy-last-updated"
        >
          <ShieldCheck className="h-3 w-3 text-[color:var(--reva-signal)]" />
          {isAr ? `آخر تحديث · ${lastUpdated}` : `Last updated · ${lastUpdated}`}
        </span>

        {isAr ? (
          <ArabicBody />
        ) : (
          <EnglishBody />
        )}

        <hr />
        <p className="text-[12px] text-[#71777E]">
          {isAr
            ? 'لأيّ استفسار، تواصل معنا على privacy@reva.sa'
            : 'For any inquiry contact privacy@reva.sa'}
        </p>
      </main>
    </div>
  );
}

function EnglishBody() {
  return (
    <>
      <h2>1. Who we are</h2>
      <p>REVA is a property operations platform operated by REVA Operations Co. (the &ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). This policy explains what personal data we collect, why we collect it, and the rights you have under the Personal Data Protection Law of the Kingdom of Saudi Arabia (PDPL).</p>

      <h2>2. Data we collect</h2>
      <ul>
        <li><b>Account data</b> — name, email, phone, organization, role.</li>
        <li><b>Operational data</b> — properties, units, leases, tenants, payments, maintenance, documents you choose to upload.</li>
        <li><b>Technical data</b> — IP address, browser, device, log timestamps.</li>
        <li><b>Communications</b> — emails, in-product messages, support requests.</li>
      </ul>

      <h2>3. Why we process it</h2>
      <ul>
        <li>To provide the REVA service to you and your tenants.</li>
        <li>To improve product reliability and security.</li>
        <li>To respond to support requests.</li>
        <li>To comply with applicable law (PDPL, ZATCA, anti-money-laundering rules).</li>
      </ul>

      <h2>4. Legal basis</h2>
      <p>We process your data based on (a) the contract you have with us, (b) your consent for optional features (e.g. WhatsApp messaging), and (c) our legitimate interest in keeping the platform secure.</p>

      <h2>5. Where data is stored</h2>
      <p>Production data is stored in a managed MongoDB cluster. Enterprise customers may request data residency inside the Kingdom of Saudi Arabia. Encrypted in transit (TLS 1.2+) and at rest.</p>

      <h2>6. Your rights (PDPL)</h2>
      <ul>
        <li>Right to be informed about how your data is used.</li>
        <li>Right of access — request a copy of your data at any time.</li>
        <li>Right to rectification — correct inaccurate data.</li>
        <li>Right to erasure — request deletion of your data (subject to retention obligations).</li>
        <li>Right to data portability — export your data in a machine-readable format.</li>
        <li>Right to object — to certain processing activities.</li>
      </ul>
      <p>Authenticated users can export their organization&rsquo;s data at any time from <i>Settings → Compliance → Export my data</i>.</p>

      <h2>7. Data retention</h2>
      <p>We retain operational data for the duration of your subscription and for 7 years after termination to comply with Saudi financial-records retention obligations. You can request earlier deletion of data outside this scope.</p>

      <h2>8. Sub-processors</h2>
      <p>We may share data with carefully selected sub-processors who help us operate REVA (cloud hosting, email delivery, payments). A current list is available on request.</p>

      <h2>9. Security</h2>
      <p>Tenant data is isolated by organization. Passwords are hashed (bcrypt). Access is logged and audited. We support 2FA for administrators.</p>

      <h2>10. International transfers</h2>
      <p>Some sub-processors may be located outside the Kingdom. Any cross-border transfer is performed under contractual safeguards aligned with PDPL.</p>

      <h2>11. Children</h2>
      <p>REVA is a B2B product and is not directed to children under 18.</p>

      <h2>12. Changes to this policy</h2>
      <p>We will notify you by email of material changes at least 30 days before they take effect.</p>
    </>
  );
}

function ArabicBody() {
  return (
    <>
      <h2>١. من نحن</h2>
      <p>REVA هي منصّة إدارة عمليّات العقارات تشغّلها شركة REVA للعمليّات («الشركة»، «نحن»). توضّح هذه السياسة البيانات الشخصيّة التي نجمعها، ولماذا نجمعها، وحقوقك بموجب نظام حماية البيانات الشخصيّة في المملكة العربيّة السعوديّة (PDPL).</p>

      <h2>٢. البيانات التي نجمعها</h2>
      <ul>
        <li><b>بيانات الحساب</b> — الاسم، البريد، الجوال، المنظّمة، الدور.</li>
        <li><b>البيانات التشغيليّة</b> — العقارات، الوحدات، العقود، المستأجرون، الدفعات، الصيانة، الوثائق.</li>
        <li><b>البيانات التقنيّة</b> — عنوان IP، المتصفّح، الجهاز، طوابع الزمن.</li>
        <li><b>المراسلات</b> — البريد، الرسائل داخل المنتج، طلبات الدعم.</li>
      </ul>

      <h2>٣. لماذا نعالج هذه البيانات</h2>
      <ul>
        <li>لتقديم خدمة REVA لك ولمستأجريك.</li>
        <li>لتحسين موثوقيّة المنتج وأمنه.</li>
        <li>للرّد على طلبات الدعم.</li>
        <li>للامتثال للأنظمة المعمول بها (PDPL، ZATCA، مكافحة غسل الأموال).</li>
      </ul>

      <h2>٤. الأساس النظاميّ</h2>
      <p>نعالج بياناتك استنادًا إلى (أ) العقد الذي يربطك بنا، (ب) موافقتك على الميّزات الاختياريّة كرسائل واتساب، (ج) مصلحتنا المشروعة في حماية المنصّة.</p>

      <h2>٥. مكان تخزين البيانات</h2>
      <p>تُخزَّن بيانات الإنتاج في عنقود MongoDB مُدار. ويمكن للعملاء المؤسّسيّين طلب إقامة البيانات داخل المملكة العربيّة السعوديّة. مشفّرة أثناء النّقل (TLS 1.2+) وفي حالة السّكون.</p>

      <h2>٦. حقوقك (PDPL)</h2>
      <ul>
        <li>الحقّ في الإحاطة بكيفيّة استخدام بياناتك.</li>
        <li>الحقّ في الوصول وطلب نسخة من بياناتك.</li>
        <li>الحقّ في تصحيح البيانات غير الصحيحة.</li>
        <li>الحقّ في طلب المحو وفق التزامات الحفظ المقرّرة.</li>
        <li>الحقّ في نقل بياناتك بصيغة قابلة للقراءة آليًّا.</li>
        <li>الحقّ في الاعتراض على بعض أنشطة المعالجة.</li>
      </ul>
      <p>يمكن للمستخدم المسجَّل تصدير بيانات منظّمته من <i>الإعدادات ← الامتثال ← تصدير بياناتي</i>.</p>

      <h2>٧. مدّة الاحتفاظ</h2>
      <p>نحتفظ بالبيانات التشغيليّة طوال مدّة الاشتراك وسبع سنوات بعد إنهائه امتثالًا لمتطلّبات حفظ السّجلّات الماليّة في المملكة. ويمكن طلب الحذف المبكِّر للبيانات خارج هذا النّطاق.</p>

      <h2>٨. المعالجون من الباطن</h2>
      <p>قد نشارك البيانات مع معالجين من الباطن مختارين بعناية (الاستضافة السّحابيّة، البريد، المدفوعات). تتوفّر القائمة الحاليّة عند الطلب.</p>

      <h2>٩. الأمان</h2>
      <p>بيانات كلّ عميل معزولة حسب المنظّمة. كلمات المرور مُشفَّرة (bcrypt). كلّ وصول مسجَّل ومُدقَّق. ونعتمد المصادقة الثنائيّة للمشرفين.</p>

      <h2>١٠. النّقل الدوليّ</h2>
      <p>قد يقع بعض المعالجين من الباطن خارج المملكة. ويتمّ أيّ نقل عابر للحدود بضمانات تعاقديّة تتوافق مع PDPL.</p>

      <h2>١١. الأطفال</h2>
      <p>REVA منتَج موجَّه للأعمال ولا يستهدف الأطفال دون الثامنة عشرة.</p>

      <h2>١٢. تعديل السّياسة</h2>
      <p>سنخطرك عبر البريد بأيّ تعديل جوهريّ قبل ثلاثين يومًا من سريانه.</p>
    </>
  );
}
